import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Drawer,
  Group,
  Loader,
  Modal,
  Checkbox,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip, rem,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCircleCheck,
  IconHash,
  IconHelp,
  IconHourglass,
  IconMessageCircle,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSend,
  IconTag,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '@/shared/components/EmptyState';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import { InlineLoader } from '@/shared/components/LoadingState';
import { TicketAttachments } from '@/features/support/AllegatiTicket';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { fetchAllPages, fetchList, createOne, updateOne } from '@/shared/api/jsonapi/client';
import { CLAIM } from '@/shared/permissions/claims';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/tickets')({ component: TicketsPage });

interface Ticket extends Denormalized {
  type: 'tickets';
  id: string;
  number?: number;
  message?: string;
  note?: string;
  status?: string;
  priority?: number;
  creationDate?: string;
  closedDate?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  answer?: string;
  ticketTag?: { id: string; name?: string } | null;
  /**
   * A chi è affidato il ticket. È un **`users`** (account), non un `erp-employees`: sono due
   * identità distinte sul BE e questa risorsa punta alla prima.
   */
  assigned?: { id: string; firstName?: string; lastName?: string; email?: string } | null;
}

interface TicketMessage extends Denormalized {
  type: 'ticket-messages';
  id: string;
  message?: string;
  creationDate?: string;
  user?: { id: string; firstName?: string; lastName?: string; email?: string } | null;
}

interface TicketTag extends Denormalized {
  type: 'ticket-tags';
  id: string;
  name?: string;
  tag?: string;
}

type StatusFilter = 'all' | 'open' | 'in-progress' | 'resolved' | 'closed';
type PriorityFilter = 'all' | '0' | '1' | '2' | '3';

import { statusColor, priorityColor } from '@/shared/lib/statusColors';
import { serverInstant } from '@/shared/lib/format';

// Label e icon locali, color canonico da statusColors.ts (palette unificata cross-prodotto).
const STATUS_META: Record<string, { label: string; icon: typeof IconHelp }> = {
  open: { label: 'Aperto', icon: IconHelp },
  'in-progress': { label: 'In lavorazione', icon: IconHourglass },
  resolved: { label: 'Risolto', icon: IconCircleCheck },
  closed: { label: 'Chiuso', icon: IconCircleCheck },
};

const PRIORITY_LABEL: Record<number, string> = {
  0: 'Bassa', 1: 'Media', 2: 'Alta', 3: 'Urgente',
};

function statusInfo(s?: string) {
  if (!s) return { label: '—', color: 'gray', icon: IconHelp };
  const meta = STATUS_META[s] ?? { label: s, icon: IconHelp };
  return { ...meta, color: statusColor(s) };
}

function priorityInfo(p?: number) {
  if (p == null) return { label: 'Media', color: priorityColor('medium') };
  return { label: PRIORITY_LABEL[p] ?? String(p), color: priorityColor(p) };
}

/** Nome di un account, col ripiego sull'email: un GUID a schermo non dice a nessuno chi sia. */
function userName(u?: { firstName?: string; lastName?: string; email?: string } | null): string {
  if (!u) return '—';
  return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || '—';
}

/**
 * Smistamento del ticket — stato, assegnatario e risposta.
 *
 * ── Perché sta qui e non in una schermata sua ──
 *
 * Era una pagina separata (`HelpDeskAdmin`, raggiungibile da due rotte diverse) che leggeva la
 * **stessa** risorsa `tickets` di questa: due elenchi degli stessi ticket, uno per chi li apre e
 * uno per chi li lavora. Accorpandole si perde la duplicazione e non le funzioni.
 *
 * ⚠️ Nel farlo si correggono due difetti che quella schermata aveva, misurati sui 41 ticket del
 * dev:
 *  · **le mancava lo stato `resolved`**, che invece esiste (3 ticket). Una `Select` con un valore
 *    fuori dalle sue opzioni si presenta vuota, e salvando avrebbe riscritto lo stato senza che
 *    nessuno l'avesse chiesto;
 *  · **il filtro priorità era sfasato di uno** (offriva `1..4`, i dati sono `0..3`): «Bassa»
 *    restituiva le Media e «Urgente» non restituiva niente. Qui si usa il vocabolario di questa
 *    pagina, che combacia coi dati.
 */
function TriagePanel({ ticket, onSaved }: { ticket: Ticket; onSaved: () => void }) {
  const [status, setStatus] = useState<string | null>(ticket.status ?? null);
  const [assignedId, setAssignedId] = useState<string | null>(ticket.assigned?.id ?? null);
  const [answer, setAnswer] = useState(ticket.answer ?? '');

  /* Il ticket cambia sotto il pannello quando si passa da un ticket all'altro senza chiudere
     il drawer: senza questo, i campi mostrerebbero i valori di quello di prima. */
  useEffect(() => {
    setStatus(ticket.status ?? null);
    setAssignedId(ticket.assigned?.id ?? null);
    setAnswer(ticket.answer ?? '');
  }, [ticket.id, ticket.status, ticket.assigned?.id, ticket.answer]);

  /*
   * `fetchAllPages` e non `page.size: 50`: la schermata di prima si fermava a 50 utenti, e sul
   * dev sono **51** — uno era già fuori dalla tendina. Con la casella ricercabile il difetto è
   * pure invisibile: cercare un nome tagliato dal tetto risponde «Nessun utente», la stessa
   * frase di un nome che non esiste.
   */
  const usersQ = useQuery({
    queryKey: ['tickets-assignable-users'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllPages<Ticket['assigned'] & { id: string; type: 'users' }>('users', {
      fields: { users: ['firstName', 'lastName', 'email'] },
      sort: ['lastName', 'firstName'],
    }),
  });

  const save = useMutation({
    mutationFn: () => {
      const attributes: Record<string, unknown> = {};
      if ((status ?? '') !== (ticket.status ?? '')) attributes.status = status ?? '';
      if (answer.trim() !== (ticket.answer ?? '').trim()) attributes.answer = answer.trim();
      /* La relationship si invia **solo se cambiata**: mandarla sempre riscriverebbe lo stesso
         valore a ogni salvataggio, e su un `null` esplicito significherebbe togliere
         l'assegnatario a chi ha solo corretto un testo. */
      const changeAssignee = (assignedId ?? null) !== (ticket.assigned?.id ?? null);
      return updateOne('tickets', {
        id: ticket.id,
        ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
        ...(changeAssignee
          ? { relationships: { assigned: { data: assignedId ? { type: 'users', id: assignedId } : null } } }
          : {}),
      });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ticket aggiornato' });
      onSaved();
    },
    onError: (err) => notifyError(err, 'Aggiornamento non riuscito'),
  });

  const nothing = (status ?? '') === (ticket.status ?? '')
    && (assignedId ?? null) === (ticket.assigned?.id ?? null)
    && answer.trim() === (ticket.answer ?? '').trim();

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Text size="xs" fw={500} tt="uppercase" c="dimmed" style={{ letterSpacing: '.08em' }}>
          Smistamento
        </Text>
        <Group grow align="flex-start">
          <Select
            label="Stato"
            data={Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label }))}
            value={status}
            onChange={setStatus}
            allowDeselect={false}
          />
          <Select
            label="Assegna a"
            placeholder={usersQ.isLoading ? 'Caricamento…' : 'Nessuno'}
            searchable
            clearable
            disabled={usersQ.isLoading}
            data={(usersQ.data ?? []).map((u) => ({ value: u!.id, label: userName(u) }))}
            value={assignedId}
            onChange={setAssignedId}
            nothingFoundMessage="Nessun utente con questo nome"
          />
        </Group>
        <Textarea
          label="Risposta"
          description="Resta sul ticket. Per parlare col richiedente c’è la conversazione qui sotto."
          autosize
          minRows={2}
          value={answer}
          onChange={(e) => setAnswer(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button size="xs" disabled={nothing} loading={save.isPending} onClick={() => save.mutate()}>
            Salva
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Chi lavora i ticket, oltre ad aprirli: vede lo stato, l'assegnatario e la risposta.
 *
 * Questa pagina è di tutti — ci si apre il proprio ticket e si conversa — quindi la parte di
 * smistamento va dietro un cancello, altrimenti chiunque potrebbe riassegnare il ticket di un
 * collega. Il cancello vero lo fa il server: qui si decide solo cosa **mostrare**, perché una
 * casella che c'è e poi risponde 403 è peggio di una casella che non c'è.
 *
 * `isAdmin` e non il permesso di rotta `r-p-help-desk-admin`: nel frontend nessuna guardia
 * applica i `r-p-*` — `ROUTE_PERMISSIONS` è solo il catalogo della schermata che li assegna.
 */
function useCanTriage(): boolean {
  const { decoded } = useAuth();
  const claims = Array.isArray(decoded?.claims) ? (decoded.claims as string[]) : [];
  return decoded?.superadmin === 'True' || claims.includes(CLAIM.admin) || claims.includes(CLAIM.hr);
}

function TicketsPage() {
  const qc = useQueryClient();
  const canTriage = useCanTriage();
  const [opened, { open, close }] = useDisclosure();
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false;
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  /** Selezione per la chiusura massiva. Solo per chi smista: vedi `canTriage`. */
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  // Create form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<string>('1');
  const [tagId, setTagId] = useState<string | null>(null);

  const ticketsQ = useQuery({
    queryKey: ['my-tickets'],
    queryFn: () => fetchList<Ticket>('tickets', {
      include: ['ticketTag', 'assigned'],
      sort: ['-creationDate'],
      page: { size: 100 },
    }),
  });

  const tagsQ = useQuery({
    queryKey: ['ticket-tags'],
    queryFn: () => fetchList<TicketTag>('ticket-tags', { page: { size: 50 } }),
    staleTime: 5 * 60_000,
  });

  const tickets = useMemo(() => ticketsQ.data?.data ?? [], [ticketsQ.data]);
  const tags = tagsQ.data?.data ?? [];

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return tickets.filter((t) => {
      if (q) {
        const hay = `${t.number ?? ''} ${t.message ?? ''} ${t.note ?? ''} ${t.ticketTag?.name ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && (t.status ?? 'open') !== statusFilter) return false;
      if (priorityFilter !== 'all' && String(t.priority ?? 1) !== priorityFilter) return false;
      return true;
    });
  }, [tickets, debouncedSearch, statusFilter, priorityFilter]);

  const counts = useMemo(() => ({
    all: tickets.length,
    open: tickets.filter((t) => (t.status ?? 'open') === 'open').length,
    'in-progress': tickets.filter((t) => t.status === 'in-progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length,
  }), [tickets]);

  /**
   * Chiusura massiva — l'unica azione della vecchia schermata di amministrazione che non
   * esisteva qui.
   *
   * Le PATCH sono **una per ticket** perché il BE non ha un endpoint di chiusura in blocco:
   * `Promise.allSettled` e non `Promise.all`, così una che fallisce non nasconde le altre che
   * sono riuscite — e il conteggio degli esiti si può dire all'utente invece di mostrargli un
   * errore secco su un'operazione andata a metà.
   */
  const closeSelected = useMutation({
    mutationFn: async (ids: string[]) => {
      const outcomes = await Promise.allSettled(
        ids.map((id) => updateOne('tickets', { id, attributes: { status: 'closed' } })),
      );
      return {
        ok: outcomes.filter((e) => e.status === 'fulfilled').length,
        ko: outcomes.filter((e) => e.status === 'rejected').length,
      };
    },
    onSuccess: ({ ok, ko }) => {
      notifications.show({
        color: ko === 0 ? 'green' : 'orange',
        message: ko === 0
          ? `${ok} ${ok === 1 ? 'ticket chiuso' : 'ticket chiusi'}`
          : `${ok} chiusi, ${ko} non riusciti: riprova su quelli rimasti aperti.`,
      });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
    },
    onError: (err) => notifyError(err, 'Chiusura non riuscita'),
  });

  const create = useMutation({
    mutationFn: () => createOne('tickets', {
      attributes: {
        message: `${title.trim()}\n\n${body.trim()}`,
        priority: parseInt(priority, 10),
        status: 'open',
      },
      relationships: tagId ? { ticketTag: { data: { type: 'ticket-tags', id: tagId } } } : undefined,
    }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ticket inviato' });
      setTitle(''); setBody(''); setTagId(null); setPriority('1');
      close();
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
    },
    onError: (err) => notifyError(err),
  });

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconHelp size={22} />}
        tone="azzurro"
        title="Help desk"
        sub="I tuoi ticket di supporto, conversazioni e aggiornamenti."
        actions={
          <>
            <Tooltip label="Aggiorna">
              <ActionIcon variant="light" size="lg" aria-label="Aggiorna lista ticket" onClick={() => qc.invalidateQueries({ queryKey: ['my-tickets'] })}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            {canTriage && selected.size > 0 && (
              <Button
                variant="default"
                loading={closeSelected.isPending}
                onClick={() => closeSelected.mutate([...selected])}
              >
                Chiudi selezionati ({selected.size})
              </Button>
            )}
            <Button leftSection={<IconPlus size={16} />} onClick={open}>Nuovo ticket</Button>
          </>
        }
      />
      {/*
        DUE POSTI PER LA STESSA DOMANDA, e questo è il vecchio.
        Dal 1 settembre 2026 l'assistenza vive sui **task** — un ticket è un task con l'argomento
        valorizzato — e ha la sua scrivania in «Assistenza (scrivania)», con i gruppi, la presa in
        carico e la conversazione che arriva al cliente sul portale. Questa pagina gira sull'entità
        `Ticket` storica, che è esattamente il modulo parallelo che il BE ha scelto di non avere:
        «due oggetti di lavoro significano due posti dove cercare cosa devo fare».
        Non si spegne di colpo perché ci sono dati dentro; ma chi arriva qui deve sapere che il
        nuovo è altrove, o continuerà a lavorare nel posto sbagliato senza che nessuno glielo dica.
      */}
      <MaeAdvice
        tone="info"
        title="Questo è l’archivio storico dell’assistenza"
        selfRemedy="Le richieste nuove si lavorano da «Assistenza (scrivania)»: lì ci sono i gruppi, la presa in carico e la conversazione che il cliente legge dal portale."
      >
        I ticket di questa pagina vivono sul vecchio help desk. Restano consultabili e rispondibili,
        ma le richieste che arrivano oggi non finiscono più qui: dal 10 settembre 2026 anche «Segnala
        un problema» e la pagina pubblica di aiuto scrivono sulla scrivania dell’assistenza. Qui
        arriva solo il ripiego, quando manca l’argomento «Piattaforma».
      </MaeAdvice>
      <Stack gap="lg">

      {isMobile && (
        <MobileTicketList
          tickets={tickets}
          filtered={filtered}
          counts={counts}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          priorityFilter={priorityFilter}
          setPriorityFilter={setPriorityFilter}
          loading={ticketsQ.isLoading}
          error={ticketsQ.isError}
          onRetry={() => void ticketsQ.refetch()}
          onOpen={setOpenTicketId}
          onNew={open}
        />
      )}

      {!isMobile && (<>
      <Card withBorder padding="md" radius="md">
        <Group wrap="wrap" gap="sm">
          <TextInput
            placeholder="Cerca per numero, testo o tag…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={260}
          />
          <SegmentedControl
            size="xs"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            data={[
              { value: 'all', label: `Tutti (${counts.all})` },
              { value: 'open', label: `Aperti (${counts.open})` },
              { value: 'in-progress', label: `In lavorazione (${counts['in-progress']})` },
              { value: 'resolved', label: `Risolti (${counts.resolved})` },
              { value: 'closed', label: `Chiusi (${counts.closed})` },
            ]}
          />
          <Select
            size="xs"
            placeholder="Priorità"
            w={140}
            value={priorityFilter}
            onChange={(v) => setPriorityFilter((v as PriorityFilter) ?? 'all')}
            data={[
              { value: 'all', label: 'Tutte priorità' },
              { value: '3', label: 'Urgente' },
              { value: '2', label: 'Alta' },
              { value: '1', label: 'Media' },
              { value: '0', label: 'Bassa' },
            ]}
          />
        </Group>
      </Card>

      <Card withBorder padding={0} radius="md">
        {ticketsQ.isError ? (
          <QueryErrorAlert error={ticketsQ.error} onRetry={() => void ticketsQ.refetch()} />
        ) : ticketsQ.isLoading ? (
          <Center py="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconHelp}
            title={tickets.length === 0 ? 'Nessun ticket aperto' : 'Nessun ticket corrisponde ai filtri'}
            description={tickets.length === 0
              ? 'Hai bisogno di assistenza? Apri un ticket: il team risponde entro 24h lavorative.'
              : 'Modifica i filtri o cerca con altri termini.'}
            actionLabel={tickets.length === 0 ? 'Nuovo ticket' : undefined}
            onAction={tickets.length === 0 ? open : undefined}
            color="teal"
          />
        ) : (
          <Paginated rows={filtered} label="ticket">
          {(pageRows) => (
          <ScrollArea type="auto">
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {canTriage && (
                    <Table.Th w={40}>
                      {/* «Tutti» = tutti quelli **della pagina mostrata**, non dei 100 caricati:
                          una spunta che seleziona anche righe che non si vedono è un modo di
                          chiudere ticket per sbaglio. */}
                      <Checkbox
                        aria-label="Seleziona tutti i ticket della pagina"
                        checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r.id))}
                        indeterminate={pageRows.some((r) => selected.has(r.id)) && !pageRows.every((r) => selected.has(r.id))}
                        onChange={(e) => setSelected((prev) => {
                          const next = new Set(prev);
                          for (const r of pageRows) { if (e.currentTarget.checked) next.add(r.id); else next.delete(r.id); }
                          return next;
                        })}
                      />
                    </Table.Th>
                  )}
                  <Table.Th w={80}>#</Table.Th>
                  <Table.Th>Oggetto</Table.Th>
                  {canTriage && <Table.Th w={170}>Autore</Table.Th>}
                  <Table.Th w={130}>Tag</Table.Th>
                  <Table.Th w={120}>Priorità</Table.Th>
                  <Table.Th w={150}>Stato</Table.Th>
                  {/* Solo per chi smista: una colonna vuota per tutti gli altri sarebbe
                      rumore, e a chi apre un ticket proprio non dice niente di utile. */}
                  {canTriage && <Table.Th w={170}>Assegnato a</Table.Th>}
                  <Table.Th w={150}>Aperto</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map((t) => {
                  const s = statusInfo(t.status);
                  const p = priorityInfo(t.priority);
                  const StatusIcon = s.icon;
                  const subject = (t.message ?? '').split('\n')[0]?.slice(0, 80) || '(senza oggetto)';
                  return (
                    <Table.Tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setOpenTicketId(t.id)}>
                      {canTriage && (
                        /* `stopPropagation`: la riga apre il dettaglio, quindi senza questo
                           spuntare la casella aprirebbe anche il drawer. */
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            aria-label={`Seleziona il ticket ${t.number ?? t.id}`}
                            checked={selected.has(t.id)}
                            onChange={(e) => setSelected((prev) => {
                              const next = new Set(prev);
                              if (e.currentTarget.checked) next.add(t.id); else next.delete(t.id);
                              return next;
                            })}
                          />
                        </Table.Td>
                      )}
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <IconHash size={12} style={{ opacity: 0.5 }} />
                          <Text size="sm" fw={500}>{t.number ?? '—'}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td><Text size="sm" lineClamp={1}>{subject}</Text></Table.Td>
                      {canTriage && (
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {[t.firstName, t.lastName].filter(Boolean).join(' ') || t.email || '—'}
                          </Text>
                        </Table.Td>
                      )}
                      <Table.Td>
                        {t.ticketTag?.name
                          ? <Badge size="sm" variant="dot" color="gray">{t.ticketTag.name}</Badge>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={p.color} variant="light">{p.label}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={s.color} leftSection={<StatusIcon size={10} />}>
                          {s.label}
                        </Badge>
                      </Table.Td>
                      {canTriage && (
                        <Table.Td>
                          {t.assigned
                            ? <Text size="sm">{userName(t.assigned)}</Text>
                            : <Badge size="sm" color="orange" variant="light">Da assegnare</Badge>}
                        </Table.Td>
                      )}
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {t.creationDate ? serverInstant(t.creationDate).format('DD/MM/YYYY HH:mm') : '—'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
          </Paginated>
        )}
      </Card>
      </>)}

      <Modal opened={opened} onClose={close} title={<Group gap={6}><IconPlus size={18} /><Text fw={600}>Nuovo ticket</Text></Group>} centered size="lg" fullScreen={isMobile}>
        <Stack>
          <TextInput
            label="Oggetto"
            placeholder="Es. Problema con login app mobile"
            required
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            data-autofocus
          />
          <Textarea
            label="Descrizione"
            placeholder="Descrivi il problema con dettaglio (azioni, errore, schermate…)"
            minRows={4}
            autosize
            required
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
          />
          <Group grow>
            <Select
              label="Priorità"
              data={[
                { value: '3', label: 'Urgente' },
                { value: '2', label: '⚠️ Alta' },
                { value: '1', label: 'Media' },
                { value: '0', label: '↘️ Bassa' },
              ]}
              value={priority}
              onChange={(v) => setPriority(v ?? '1')}
              allowDeselect={false}
            />
            <Select
              label="Categoria"
              placeholder="Tag…"
              data={tags.map((t) => ({ value: t.id, label: t.name ?? t.tag ?? t.id }))}
              value={tagId}
              onChange={setTagId}
              searchable
              clearable
              leftSection={<IconTag size={14} />}
            />
          </Group>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={14} />}>
            Riceverai una notifica appena un operatore prende in carico il ticket.
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>Annulla</Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={!title.trim() || !body.trim()}
              leftSection={<IconSend size={14} />}
            >
              Invia ticket
            </Button>
          </Group>
        </Stack>
      </Modal>

      <TicketDetailDrawer
        ticketId={openTicketId}
        onClose={() => setOpenTicketId(null)}
      />
      </Stack>
    </MaeShell>
  );
}

interface MobileTicketListProps {
  tickets: Ticket[];
  filtered: Ticket[];
  counts: Record<string, number>;
  search: string;
  setSearch: (s: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (s: StatusFilter) => void;
  priorityFilter: PriorityFilter;
  setPriorityFilter: (p: PriorityFilter) => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpen: (id: string) => void;
  onNew: () => void;
}

const MOBILE_STATUS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Tutti' },
  { value: 'open', label: 'Aperti' },
  { value: 'in-progress', label: 'In lavoraz.' },
  { value: 'resolved', label: 'Risolti' },
  { value: 'closed', label: 'Chiusi' },
];

/** Toolbar + lista ticket in versione mobile (card list al posto della tabella). */
function MobileTicketList(props: MobileTicketListProps) {
  const { tickets, filtered, counts, search, setSearch, statusFilter, setStatusFilter, priorityFilter, setPriorityFilter, loading, error, onRetry, onOpen, onNew } = props;
  return (
    <>
      <Group gap={10} wrap="nowrap" align="center">
        <TextInput
          style={{ flex: 1 }}
          placeholder="Cerca numero, testo, tag…"
          leftSection={<IconSearch size={16} />}
          rightSection={search ? <ActionIcon variant="subtle" size="sm" aria-label="Cancella" onClick={() => setSearch('')}><IconX size={12} /></ActionIcon> : null}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          radius="md"
        />
        <Select
          w={128}
          placeholder="Priorità"
          value={priorityFilter}
          onChange={(v) => setPriorityFilter((v as PriorityFilter) ?? 'all')}
          allowDeselect={false}
          data={[
            { value: 'all', label: 'Tutte' },
            { value: '3', label: 'Urgente' },
            { value: '2', label: 'Alta' },
            { value: '1', label: 'Media' },
            { value: '0', label: 'Bassa' },
          ]}
        />
      </Group>

      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 2px' }}>
        {MOBILE_STATUS.map((s) => {
          const active = s.value === statusFilter;
          return (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className="mw-pressable"
              style={{
                flexShrink: 0, height: rem(34), padding: '0 14px', borderRadius: 999, cursor: 'pointer',
                fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit',
                border: active ? `1px solid ${mw.brand}` : `1px solid ${mw.line}`,
                background: active ? mw.brandFill : mw.surface, color: active ? 'var(--on-brand)' : mw.ink500,
              }}
            >
              {s.label} · <span style={{ fontVariantNumeric: 'tabular-nums' }}>{counts[s.value] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {error ? (
        <QueryErrorAlert error={new Error('Errore caricamento ticket')} onRetry={onRetry} />
      ) : loading ? (
        <Center py="xl"><Loader /></Center>
      ) : filtered.length === 0 ? (
        <div style={{ border: `1px solid ${mw.line}`, borderRadius: mw.radiusCard, background: mw.surface, boxShadow: MW_CARD_SHADOW }}>
          <EmptyState
            icon={IconHelp}
            title={tickets.length === 0 ? 'Nessun ticket aperto' : 'Nessun ticket coi filtri'}
            description={tickets.length === 0 ? 'Apri un ticket: il team risponde entro 24h lavorative.' : 'Modifica filtri o ricerca.'}
            actionLabel={tickets.length === 0 ? 'Nuovo ticket' : undefined}
            onAction={tickets.length === 0 ? onNew : undefined}
            color="teal"
          />
        </div>
      ) : (
        <Stack gap={10}>
          {filtered.map((t) => {
            const s = statusInfo(t.status);
            const p = priorityInfo(t.priority);
            const StatusIcon = s.icon;
            const subject = (t.message ?? '').split('\n')[0]?.slice(0, 100) || '(senza oggetto)';
            return (
              <button
                key={t.id}
                onClick={() => onOpen(t.id)}
                className="mw-pressable"
                style={{ textAlign: 'left', cursor: 'pointer', width: '100%', border: `1px solid ${mw.line}`, borderRadius: mw.radiusTile, background: mw.surface, boxShadow: MW_CARD_SHADOW, padding: 14 }}
              >
                <Group justify="space-between" wrap="nowrap" mb={6}>
                  <Group gap={4} wrap="nowrap">
                    <IconHash size={13} style={{ opacity: 0.5 }} />
                    <Text size="sm" fw={700} style={{ color: mw.ink900, fontVariantNumeric: 'tabular-nums' }}>{t.number ?? '—'}</Text>
                  </Group>
                  <Badge size="sm" color={s.color} leftSection={<StatusIcon size={10} />}>{s.label}</Badge>
                </Group>
                <Text size="sm" style={{ color: mw.ink900 }} lineClamp={2}>{subject}</Text>
                <Group gap={6} wrap="wrap" mt={8} align="center">
                  <Badge size="sm" color={p.color} variant="light">{p.label}</Badge>
                  {t.ticketTag?.name && <Badge size="sm" variant="dot" color="gray">{t.ticketTag.name}</Badge>}
                  <div style={{ flex: 1 }} />
                  <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>{t.creationDate ? serverInstant(t.creationDate).format('DD/MM/YY HH:mm') : '—'}</Text>
                </Group>
              </button>
            );
          })}
        </Stack>
      )}
    </>
  );
}

function TicketDetailDrawer({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canTriage = useCanTriage();
  const [reply, setReply] = useState('');

  const ticketQ = useQuery({
    queryKey: ['ticket', ticketId],
    enabled: Boolean(ticketId),
    queryFn: async () => {
      const { data } = await fetchList<Ticket>('tickets', {
        filter: { id: { eq: ticketId! } },
        include: ['ticketTag', 'assigned'],
        page: { size: 1 },
      });
      return data[0] ?? null;
    },
  });

  const messagesQ = useQuery({
    queryKey: ['ticket-messages', ticketId],
    enabled: Boolean(ticketId),
    queryFn: async () => {
      try {
        const { data } = await fetchList<TicketMessage>('ticket-messages', {
          filter: { 'ticket.id': { eq: ticketId! } },
          include: ['user'],
          sort: ['creationDate'],
          page: { size: 100 },
        });
        return data;
      } catch {
        return [];
      }
    },
  });

  const reset = () => { setReply(''); };

  const sendReply = useMutation({
    mutationFn: () => createOne('ticket-messages', {
      attributes: { message: reply.trim() },
      relationships: { ticket: { data: { type: 'tickets', id: ticketId! } } },
    }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Risposta inviata' });
      reset();
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
    },
    onError: (err) => notifyError(err, 'Errore invio'),
  });

  const ticket = ticketQ.data;

  // NON fare `if (!ticketId) return null`: il Drawer si monterebbe già `opened` e
  // l'animazione di ingresso non partirebbe (la transizione ha bisogno del passaggio
  // false → true su un componente già montato).
  return (
    <Drawer
      opened={Boolean(ticketId)}
      onClose={() => { reset(); onClose(); }}
      position="right"
      size="lg"
      transitionProps={{ transition: 'slide-left', duration: 240, timingFunction: 'cubic-bezier(0.2,0,0,1)' }}
      title={
        <Group gap={6}>
          <ActionIcon variant="subtle" aria-label="Indietro" onClick={onClose}>
            <IconArrowLeft size={16} />
          </ActionIcon>
          <IconHash size={14} style={{ opacity: 0.5 }} />
          <Text fw={600}>{ticket?.number ?? '…'}</Text>
        </Group>
      }
    >
      {ticketQ.isLoading || !ticket ? (
        <Center py="xl"><Loader /></Center>
      ) : (
        <Stack>
          {/* Header con metadati */}
          <Card withBorder padding="md" radius="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap={6}>
                  {(() => {
                    const s = statusInfo(ticket.status);
                    const SIcon = s.icon;
                    return (
                      <Badge color={s.color} leftSection={<SIcon size={10} />}>
                        {s.label}
                      </Badge>
                    );
                  })()}
                  {(() => {
                    const p = priorityInfo(ticket.priority);
                    return <Badge color={p.color} variant="light">{p.label}</Badge>;
                  })()}
                  {ticket.ticketTag?.name && (
                    <Badge variant="dot" color="gray">{ticket.ticketTag.name}</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {ticket.creationDate ? serverInstant(ticket.creationDate).format('DD/MM/YYYY HH:mm') : '—'}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Aperto da: <strong>{[ticket.firstName, ticket.lastName].filter(Boolean).join(' ') || ticket.email || '—'}</strong>
              </Text>
              {ticket.closedDate && (
                <Text size="xs" c="dimmed">
                  Chiuso il: {serverInstant(ticket.closedDate).format('DD/MM/YYYY HH:mm')}
                </Text>
              )}
              {/* L'assegnatario si vede anche da non-amministratore: chi ha aperto il ticket
                  ha diritto di sapere se qualcuno lo ha preso in carico. Cambiarlo, no. */}
              <Text size="xs" c="dimmed">
                Assegnato a:{' '}
                <strong>{ticket.assigned ? userName(ticket.assigned) : 'nessuno'}</strong>
              </Text>
            </Stack>
          </Card>

          {canTriage && <TriagePanel ticket={ticket} onSaved={() => {
            qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
            qc.invalidateQueries({ queryKey: ['my-tickets'] });
          }} />}

          {/* Messaggio iniziale */}
          <ConversationBubble
            author="Tu"
            initials={(user?.firstName?.[0] ?? user?.email?.[0] ?? 'T').toUpperCase()}
            color="blue"
            date={ticket.creationDate}
            body={ticket.message ?? ''}
          />

          {/* Gli allegati della segnalazione, subito sotto il messaggio a cui appartengono.
              Senza questo pannello i file arrivavano e nessuno li vedeva. */}
          {ticketId && <TicketAttachments ticketId={ticketId} />}

          {/* Conversazione */}
          {messagesQ.isLoading ? (
            <InlineLoader label="Caricamento della conversazione…" minHeight={80} />
          ) : (
            (messagesQ.data ?? []).map((m) => {
              const author = m.user
                ? [m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || m.user.email || 'Operatore'
                : 'Operatore';
              const initials = m.user?.firstName?.[0]?.toUpperCase() ?? 'O';
              return (
                <ConversationBubble
                  key={m.id}
                  author={author}
                  initials={initials}
                  color="teal"
                  date={m.creationDate}
                  body={m.message ?? ''}
                />
              );
            })
          )}

          {/* Risposta ufficiale (se presente) */}
          {ticket.answer && (
            <Card withBorder padding="md" radius="md" bg="var(--mantine-color-green-light)">
              <Text size="xs" fw={600} c="green" mb={4}>Risposta ufficiale</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{ticket.answer}</Text>
            </Card>
          )}

          {/* Reply form */}
          {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
            <Card withBorder padding="md" radius="md">
              <Group gap={6} mb="xs">
                <IconMessageCircle size={14} />
                <Text size="sm" fw={500}>Aggiungi una risposta</Text>
              </Group>
              <Textarea
                placeholder="Scrivi un aggiornamento o una nuova informazione…"
                autosize
                minRows={3}
                value={reply}
                onChange={(e) => setReply(e.currentTarget.value)}
              />
              <Group justify="flex-end" mt="xs">
                <Button
                  size="sm"
                  leftSection={<IconSend size={14} />}
                  disabled={!reply.trim()}
                  loading={sendReply.isPending}
                  onClick={() => sendReply.mutate()}
                >
                  Invia
                </Button>
              </Group>
            </Card>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function ConversationBubble({
  author, initials, color, date, body,
}: {
  author: string;
  initials: string;
  color: string;
  date?: string;
  body: string;
}) {
  return (
    <Group align="flex-start" wrap="nowrap" gap="sm">
      <Avatar size="md" radius="xl" color={color}>{initials}</Avatar>
      <Card withBorder padding="sm" radius="md" style={{ flex: 1 }}>
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>{author}</Text>
          {date && (
            <Text size="xs" c="dimmed">{serverInstant(date).format('DD/MM HH:mm')}</Text>
          )}
        </Group>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{body || <Text component="span" c="dimmed">— vuoto —</Text>}</Text>
      </Card>
    </Group>
  );
}

